import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Award, Sparkles, Save } from 'lucide-react';
import { toast } from 'sonner';
import { BadgePill } from './BadgeDisplay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Trust & Social Proof admin panel embedded inside EditTourPage.
 * Lets admin:
 *   - assign/unassign badges on the tour
 *   - toggle "featured" flag
 *   - set popularity threshold (below which "New Tour" label shows)
 */
const TourTrustAdmin = ({ tourId, token }) => {
  const [loading, setLoading] = useState(true);
  const [allBadges, setAllBadges] = useState([]);
  const [assignedIds, setAssignedIds] = useState([]);
  const [featured, setFeatured] = useState(false);
  const [threshold, setThreshold] = useState(15);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [badgesRes, tourRes] = await Promise.all([
        axios.get(`${API}/admin/badges`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/tours/${tourId}`),
      ]);
      setAllBadges(badgesRes.data || []);
      const t = tourRes.data || {};
      const assigned = (t.badges || []).map((b) => b.badge_id);
      setAssignedIds(assigned);
      setFeatured(!!t.featured);
      setThreshold(
        typeof t.popularity_threshold === 'number' ? t.popularity_threshold : 15
      );
    } catch {
      toast.error('Failed to load trust settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tourId && token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourId, token]);

  const toggleBadge = (id) => {
    setAssignedIds((cur) =>
      cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      await Promise.all([
        axios.put(
          `${API}/admin/tours/${tourId}/badges`,
          { badge_ids: assignedIds },
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios.put(
          `${API}/admin/tours/${tourId}/featured`,
          { featured, popularity_threshold: Number(threshold) || 0 },
          { headers: { Authorization: `Bearer ${token}` } }
        ),
      ]);
      toast.success('Trust settings saved');
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border-4 border-rose-300 rounded-2xl shadow-card p-8 mb-6">
        <p className="text-gray-500">Loading trust settings…</p>
      </div>
    );
  }

  return (
    <div className="bg-white border-4 border-rose-300 rounded-2xl shadow-card p-8 mb-6" data-testid="tour-trust-admin">
      <h2 className="text-2xl font-black uppercase text-gray-900 mb-2 flex items-center gap-2">
        <Award className="w-6 h-6 text-rose-500" />
        Trust &amp; Social Proof
      </h2>
      <p className="text-gray-600 mb-6">
        Assign credibility badges to this tour, toggle featured status, and set the popularity threshold
        that controls when the <strong>New Tour</strong> label appears.
      </p>

      {/* Featured + Threshold */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <label className="flex items-start gap-3 p-4 bg-cyan-50 rounded-xl cursor-pointer">
          <input
            type="checkbox"
            checked={featured}
            onChange={(e) => setFeatured(e.target.checked)}
            data-testid="tour-featured-checkbox"
            className="mt-1 w-5 h-5 rounded"
          />
          <div>
            <p className="font-bold text-gray-900 flex items-center gap-1">
              <Sparkles className="w-4 h-4 text-cyan-600" /> Featured Tour
            </p>
            <p className="text-sm text-gray-600">Highlighted with a "Featured" pill on the tour page.</p>
          </div>
        </label>
        <div className="p-4 bg-amber-50 rounded-xl">
          <label className="block text-sm font-bold text-gray-800 mb-1">
            "New Tour" label threshold
          </label>
          <p className="text-xs text-gray-600 mb-2">
            Show "New Tour" label while total starts are below this number.
          </p>
          <input
            type="number"
            min={0}
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
            data-testid="popularity-threshold-input"
            className="w-full px-4 py-2 rounded-lg border-2 border-amber-300 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
          />
        </div>
      </div>

      {/* Badge selector */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-gray-900">Credibility Badges</h3>
          <a
            href="/admin/badges"
            className="text-xs text-cyan-700 underline hover:text-cyan-900"
          >
            Manage badge library →
          </a>
        </div>
        {allBadges.length === 0 ? (
          <p className="text-sm text-gray-500">
            No badges exist yet. Create some in <a href="/admin/badges" className="underline text-cyan-700">Badge Library</a>.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="badge-selector">
            {allBadges.map((b) => {
              const selected = assignedIds.includes(b.badge_id);
              return (
                <button
                  key={b.badge_id}
                  type="button"
                  onClick={() => toggleBadge(b.badge_id)}
                  data-testid={`badge-toggle-${b.badge_id}`}
                  className={`transition-all ${
                    selected ? 'ring-2 ring-cyan-500 ring-offset-1 rounded-full' : 'opacity-60 hover:opacity-100'
                  }`}
                  title={selected ? 'Click to unassign' : 'Click to assign'}
                >
                  <BadgePill badge={b} />
                </button>
              );
            })}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={save}
        disabled={saving}
        data-testid="save-tour-trust-btn"
        className="bg-rose-500 hover:bg-rose-600 text-white px-6 py-3 rounded-lg font-bold flex items-center gap-2 disabled:opacity-50"
      >
        <Save className="w-4 h-4" />
        {saving ? 'Saving…' : 'Save Trust Settings'}
      </button>
    </div>
  );
};

export default TourTrustAdmin;
